// Promo code admin service (promo_master CRUD).
// Runs on the API gateway like every other beadmin domain: encrypted request +
// decrypted response, beadmin auth headers, on /app/v1/beadmin paths (via
// apiClient). Gateway routes forward to the booking engine's admin promo API:
//   getPromosAPI     -> GET  /app/v1/beadmin/promos           (engine GET  /be/v1/admin/promos)
//   savePromoAPI     -> POST /app/v1/beadmin/promos/save      (engine POST /be/v1/admin/promos/save)
//   updatePromoAPI   -> POST /app/v1/beadmin/promos/update/:id(engine POST /be/v1/admin/promos/update/:id)
//   setPromoStatusAPI-> POST /app/v1/beadmin/promos/status/:id(engine POST /be/v1/admin/promos/status/:id)
//
// Envelope: the engine ALWAYS returns HTTP 200 and puts the logical result in
// body.status (200 ok | 400 validation | 404 not found | 409 duplicate). Callers
// must branch on the returned envelope's `.status`, not the HTTP code:
//   200 -> `.data` is the promo (or array for list)
//   400 -> `.data` is an array of error-message strings
//   404 -> bad promo_id ; 409 -> duplicate promo_code
// See docs/PROMO_GATEWAY_SPEC.md.
import { BE_ADMIN_BASE } from "./environment";
import { apiGet, apiPost } from "./apiClient";
import { getUserDetails } from "./authSession";

// Who is creating/editing — mirrors getAgentId() in CancelBookingWorkflow.tsx /
// ModifyBookingWorkflow.tsx. Used for created_by / modified_by.
export const getAdminId = () => {
  const u = getUserDetails();
  return u?.email || u?.userId || u?.id || "admin";
};

// Normalize to the engine envelope { status, message, data }. The gateway may
// either preserve that shape as-is, or wrap it as
// { statusCode, message, data: { status, message, data } } (as the flight-booking
// route does). Detect the wrapper by the inner `.data.status` and peel it off.
const unwrap = (res) =>
  res && res.data && res.data.status !== undefined ? res.data : res;

/**
 * List promos, optionally filtered.
 * @param {object} [params] { is_active, discount_type, promo_code, search, active_now }
 * @returns {Promise<{status:number,message:string,data:Array}>}
 */
export const getPromosAPI = (params = {}, opts = {}) => {
  const qs = new URLSearchParams();
  if (params?.is_active !== undefined && params.is_active !== "")
    qs.set("is_active", params.is_active);
  if (params?.discount_type) qs.set("discount_type", params.discount_type);
  if (params?.promo_code) qs.set("promo_code", params.promo_code);
  if (params?.search) qs.set("search", params.search);
  if (params?.active_now) qs.set("active_now", params.active_now);
  const q = qs.toString();
  return apiGet(`${BE_ADMIN_BASE}/promos${q ? `?${q}` : ""}`, opts).then(unwrap);
};

/**
 * Create a promo. `payload` must include created_by (use getAdminId()).
 * @returns {Promise<{status:number,message:string,data:object|string[]}>}
 */
export const savePromoAPI = (payload, opts = {}) =>
  apiPost(`${BE_ADMIN_BASE}/promos/save`, payload, opts).then(unwrap);

/**
 * Partial update of a promo by promo_id. `payload` must include modified_by.
 * promo_code / promo_usage_count are ignored server-side.
 * @returns {Promise<{status:number,message:string,data:object|string[]}>}
 */
export const updatePromoAPI = (id, payload, opts = {}) =>
  apiPost(`${BE_ADMIN_BASE}/promos/update/${id}`, payload, opts).then(unwrap);

/**
 * Activate / deactivate a promo by promo_id.
 * @param {string} id promo_id
 * @param {object} payload { is_active, modified_by }
 * @returns {Promise<{status:number,message:string,data:object}>}
 */
export const setPromoStatusAPI = (id, payload, opts = {}) =>
  apiPost(`${BE_ADMIN_BASE}/promos/status/${id}`, payload, opts).then(unwrap);

export default {
  getPromosAPI,
  savePromoAPI,
  updatePromoAPI,
  setPromoStatusAPI,
  getAdminId,
};
